import React from "react";
import { Box, Stack, Skeleton, Typography } from "@mui/material";
import { CardWithImage, InternatlServerError, LoadingSection, NotFoundSection } from "../../components";
import { useLazyGetNounsQuery } from "../../redux/services";
import { groupBy } from "../../utils";
import copy from "fast-copy";
import { StyledDataGrid } from "../workbook/actions/actionTable";
import { GridCellParams, GridEventListener, GridRenderCellParams, MuiEvent } from "@mui/x-data-grid-premium";

export const NounListing: React.FC<{
    onNounSelect: any,
    uom: any,
    selectedNoun?: string,
    isGrid?: boolean
}> = ({ onNounSelect, uom, selectedNoun = "", isGrid = true }) => {

    const [refetch, { status, isFetching, isLoading, isError, isSuccess, data: allData = { data: [] } }] = useLazyGetNounsQuery({});

    const loading = isFetching || isLoading;

    let Columns = React.useMemo(() => {
        return [
            {
                field: "attachments",
                headerName: "Noun Image",
                width: 150,
                renderCell: (params: GridRenderCellParams) => (
                    <Box>
                        <img
                            height={"50px"}
                            width={"50px"}
                            style={{ objectFit: "contain" }}
                            src={
                                params?.row?.attachments
                                    ? params?.row?.attachments?.at(-1)?.url
                                    : `/imageNotFound.jpg`
                            }
                            alt={params?.row?.attachments?.at(-1)?.file_name ?? ""}
                        />
                    </Box>
                ),
                disableColumnMenu: true,
                sortable: false,
            },
            {
                field: "noun_name",
                headerName: "Noun Name",
                width: 250,
                renderCell: (params: GridRenderCellParams) => {
                    if (params.rowNode.type === "group") {
                        return params.value;
                    }
                    return <Typography variant="body2">{params.row?.noun_name}</Typography>;
                },
            },
            {
                field: "group_name",
                headerName: "Group Name",
                width: 200,
                renderCell: (params: GridRenderCellParams) => {
                    if (params.rowNode.type === "group") {
                        return params.value;
                    }
                    return <Typography variant="body2">{params.row?.group_name}</Typography>;
                },
            },
        ];
        // eslint-disable-next-line
    }, [status])

    const giveMeNouns = () => {
        if (isSuccess && allData?.data?.length) {
            let newData = copy(allData?.data);
            if (uom?.value) {
                newData = newData?.filter((_: any) => _.uom_id === uom.value);
            }
            return groupBy(newData?.sort((a: any, b: any) => a?.group_name?.localeCompare(b?.group_name)), 'group_id');
        }
        return {}
    }

    const nounsObj = giveMeNouns();

    const handleRowClick: GridEventListener<'rowClick'> = (params) => {
        onNounSelect({ ...params.row, value: params.row?._id, label: params.row?.noun_name  })
    };

    const getComponent = (isGrid: boolean) => {
        if (isGrid) {
            return (Object.keys(nounsObj).map((groupId: any, key: number) => {
                let nouns = nounsObj[groupId];
                return (
                    <Box key={key} sx={{
                        width: "100%"
                    }}>
                        <Typography gutterBottom variant="body2" sx={{ padding: 1, background: (theme) => theme.palette.grey[200], borderRadius: 1, fontWeight: "bold", fontFamily: "htrts_regular" }} >
                            {nouns[0].group_name}
                        </Typography>
    
                        <Stack
                            direction={"row"}
                            sx={{
                                flexWrap: "wrap",
                                mt: 1
                            }}>
                            {nouns.sort((a: any, b: any) => a.noun_name.localeCompare(b.noun_name)).map((noun: any, key: string) => {
                                return <CardWithImage
                                    id={`noun-${key}`}
                                    key={key}
                                    height={150}
                                    data={{ ...noun, value: noun._id, label: noun.noun_name }}
                                    onImageClick={onNounSelect}
                                    image={noun.attachments?.[0]?.url}
                                    title={noun?.noun_name ?? "noun " + key}
                                    isSelected={selectedNoun === noun._id}
                                />
                            })}
                        </Stack>
                    </Box>
                );
            }));
        } else {
            let ROWS = [...(allData?.data ?? [])]?.sort((a: any, b: any) => a?.group_name?.localeCompare(b?.group_name));
            if (uom) {
                ROWS = ROWS?.filter((_: any) => _.uom_id === uom.value)
            }

            return (<>
                <Stack
                    sx={{
                        width: "100%",
                        alignItems: "center",
                        justifyContent: "center"
                    }}>
                    {loading && <LoadingSection message={'Fetching Nouns...'} />}
                </Stack>
                {!loading && isSuccess && allData?.data?.length > 0 &&
                    <StyledDataGrid
                        id="noun-dg-list-view"
                        sx={{
                            '.MuiDataGrid-row': {
                                cursor: "pointer"
                            },
                            '.MuiDataGrid-cell:focus': {
                                outlineColor: "transparent !important"
                            },
                            '.MuiDataGrid-virtualScroller': {
                                overflow: 'auto !important',
                                height: "68vh !important"
                            },
                            '.MuiDataGrid-columnHeaders': {
                                position: 'sticky',
                                top: 0,
                                zIndex: 100,
                                backgroundColor: 'white',
                            },

                        }}
                        rows={ROWS}
                        columns={Columns}
                        loading={loading}
                        getRowId={(row) => row._id}
                        onRowClick={handleRowClick}
                        onCellClick={(params: GridCellParams, event: MuiEvent<React.MouseEvent>) => {
                            event.defaultMuiPrevented = true;
                        }}
                        pagination={false}
                    />}
            </>);
        }
    }

    // eslint-disable-next-line
    React.useEffect(() => { refetch({ page: null, pageSize: null }) }, []);

    return (
        <Box
            sx={{
                p: 1,
                height: "100%",
                width: '100%',
                overflowY: "scroll",
                overflowX: "hidden",
                boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px',
                background: (theme) => theme.palette.background.paper,
                borderRadius: 2
            }}>

            {/* Nouns Listing Title */}
            {/* <Typography gutterBottom variant="body2" sx={{ padding: 1, background: (theme) => theme.palette.grey[200], borderRadius: 1, fontWeight: "bold", fontFamily: "htrts_regular" }} >
                All Nouns
            </Typography> */}

            {/* Nouns Listing */}
            <Stack
                direction={"row"}
                sx={{
                    flexWrap: "wrap"
                }}
            >
                {isLoading && Array.from(Array(24).keys()).map((arr, key) => {
                    return (
                        <Box
                            key={key}
                            sx={{
                                height: "100px",
                                mb: 1,
                                mr: 1,
                                width: { xs: "100%", sm: "100px" }
                            }}>
                            <Skeleton variant="rectangular" width={"100%"} height={100} />
                        </Box>
                    );
                })}

                {!isLoading && isError && <InternatlServerError message={'Opps! Something Went Wrong, Unable to fetch Nouns'} onRetry={() => refetch({ page: null, pageSize: null })} />}
                {!isLoading && !isError && allData?.data?.length === 0 && <NotFoundSection message='You are yet to create nouns.' />}
                {!isLoading && isSuccess && getComponent(isGrid)}
            </Stack>
        </Box>
    );
}